import { Injectable, signal, TemplateRef } from '@angular/core';

// Generic confirmation configuration interface
export interface ConfirmationConfig<T = unknown> {
  title: string;
  message: string;
  formData?: T; // Generic form data
  showFormData?: boolean; // Whether to show form data preview

  // Button actions - buttons are visible only if their action is provided
  onNext?: (data?: T) => void; // Next button action
  nextButtonText?: string; // Text for Next button (default: 'Next')
  nextButtonDisabled?: boolean; // Whether Next button is disabled
  nextButtonClass?: string; // Optional custom CSS class for the Next button

  onSave?: (data?: T) => void; // Save button action
  saveButtonText?: string; // Text for Save button (default: 'Save')
  saveButtonDisabled?: boolean; // Whether Save button is disabled
  saveButtonClass?: string; // Optional custom CSS class for the Save button

  onSaveEdits?: (data?: T) => void; // Save Draft/Edits button action
  saveEditsButtonText?: string; // Text for Save Edits button (default: 'Save Draft')
  saveEditsButtonDisabled?: boolean; // Whether Save Edits button is disabled
  saveEditsButtonClass?: string; // Optional custom CSS class for the Save Edits button

  onCancel?: (data?: T) => void; // Cancel button action
  cancelButtonText?: string; // Text for Cancel button (default: 'Cancel')
  cancelButtonDisabled?: boolean; // Whether Cancel button is disabled

  onBack?: (data?: T) => void; // Back button action
  backButtonText?: string; // Text for Back button (default: 'Back')
  backButtonDisabled?: boolean; // Whether Back button is disabled

  // Legacy support (deprecated - use onNext instead)
  primaryButtonText?: string;
  primaryButtonDisabled?: boolean;
  primaryTemplate?: TemplateRef<unknown>;
  primaryAction?: (data?: T) => void;

  // Legacy support (deprecated - use onCancel instead)
  secondaryButtonText?: string;
  secondaryAction?: (data?: T) => void;

  // Legacy support (deprecated - use onBack instead)
  backButtonTemplate?: TemplateRef<unknown>;
  backAction?: (data?: T) => void;
}

/**
 * Generic Confirmation Service
 *
 * Usage Examples:
 *
 * 1. Always visible confirmation (like create user page):
 *    ngOnInit() {
 *      this.confirmationService.showConfirmationDialog({
 *        title: 'Page Actions',
 *        message: 'Choose an action',
 *        primaryButtonText: 'Save',
 *        secondaryButtonText: 'Cancel',
 *        primaryAction: () => this.save(),
 *        secondaryAction: () => this.cancel()
 *      });
 *    }
 *
 * 2. On-demand confirmation:
 *    onDelete() {
 *      this.confirmationService.showConfirmationDialog({
 *        title: 'Delete Item',
 *        message: 'Are you sure you want to delete this item?',
 *        primaryButtonText: 'Delete',
 *        secondaryButtonText: 'Cancel',
 *        primaryAction: () => this.deleteItem(),
 *        secondaryAction: () => {}
 *      });
 *    }
 *
 * 3. Form confirmation with data preview:
 *    onSubmit() {
 *      this.confirmationService.showFormConfirmation(formData, {
 *        title: 'Confirm Submission',
 *        message: 'Review your data before submitting',
 *        primaryButtonText: 'Submit',
 *        secondaryButtonText: 'Edit',
 *        primaryAction: (data) => this.submit(data),
 *        secondaryAction: () => {}
 *      });
 *    }
 */
@Injectable({
  providedIn: 'root',
})
export class ConfirmationService {
  // State signals
  private showConfirmationSignal = signal<boolean>(false);
  private confirmationConfigSignal = signal<ConfirmationConfig<unknown> | null>(
    null
  );

  // Getters for components
  get showConfirmation() {
    return this.showConfirmationSignal;
  }

  get confirmationConfig() {
    return this.confirmationConfigSignal;
  }

  // Generic method to show confirmation
  showConfirmationDialog<T>(config: ConfirmationConfig<T>): void {
    this.confirmationConfigSignal.set(config as ConfirmationConfig<unknown>);
    this.showConfirmationSignal.set(true);
  }

  // Generic form confirmation helper
  showFormConfirmation<T>(
    formData: T,
    config: Omit<ConfirmationConfig<T>, 'formData'>
  ): void {
    this.showConfirmationDialog({
      ...config,
      formData,
      showFormData: true,
    });
  }

  // Hide confirmation
  hideConfirmation(): void {
    this.showConfirmationSignal.set(false);
    this.confirmationConfigSignal.set(null);
  }

  // Event handlers for component
  onNextClick(): void {
    const config = this.confirmationConfig();
    const action = config?.onNext || config?.primaryAction; // Support legacy
    const disabled =
      config?.nextButtonDisabled ?? config?.primaryButtonDisabled ?? false;
    if (action && !disabled) {
      action(config.formData);
    }
  }

  onSaveClick(): void {
    const config = this.confirmationConfig();
    if (config?.onSave && !config.saveButtonDisabled) {
      config.onSave(config.formData);
    }
  }

  onSaveEditsClick(): void {
    const config = this.confirmationConfig();
    if (config?.onSaveEdits && !config.saveEditsButtonDisabled) {
      config.onSaveEdits(config.formData);
    }
  }

  onCancelClick(): void {
    const config = this.confirmationConfig();
    const action = config?.onCancel || config?.secondaryAction; // Support legacy
    if (action) {
      action(config.formData);
    }
  }

  onBackClick(): void {
    const config = this.confirmationConfig();
    const action = config?.onBack || config?.backAction; // Support legacy
    const disabled = config?.backButtonDisabled ?? false;
    if (action && !disabled) {
      action(config.formData);
    }
  }

  // Legacy handlers (deprecated)
  onPrimaryClick(): void {
    this.onNextClick();
  }

  onSecondaryClick(): void {
    this.onCancelClick();
  }
}
